import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'src/redux/redux-state';
import type { SurveyGroup } from 'src/types/survey-group.types';
import {
  useDeleteSurveyGroupMutation,
  useGetSurveyGroupsQuery,
} from 'src/redux/survey/survey-groups.endpoints';
import { apiSurveyGroupDetailToSurveyGroup } from 'src/services/survey-groups/survey-group-api-adapter';
import { getIsUserClient } from 'src/redux/user/user.selectors';

export function useSurveyGroups() {
  const access = useSelector((s: ReduxState) => s.userState.access);
  const isClientUser = useSelector(getIsUserClient);

  const { data: apiGroups, refetch, isFetching, isLoading } = useGetSurveyGroupsQuery(undefined, {
    skip: !access || isClientUser,
  });

  const [deleteSurveyGroup] = useDeleteSurveyGroupMutation();

  const groups: SurveyGroup[] = useMemo(
    () => (apiGroups ?? []).map(apiSurveyGroupDetailToSurveyGroup),
    [apiGroups]
  );

  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const removeGroup = useCallback(
    async(groupId: string) => {
      await deleteSurveyGroup(groupId).unwrap();
    },
    [deleteSurveyGroup]
  );

  return { groups, removeGroup, refresh, isFetching, isLoading };
}
